// 图片缓存管理器
class ImageCache {
  private cache = new Map<string, HTMLImageElement>();
  private loadingPromises = new Map<string, Promise<HTMLImageElement>>();

  async loadImage(url: string): Promise<HTMLImageElement> {
    // 如果已经缓存，直接返回
    if (this.cache.has(url)) {
      return this.cache.get(url)!;
    }

    // 如果正在加载，返回同一个 Promise
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url)!;
    }

    // 创建新的加载 Promise
    const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.cache.set(url, img);
        this.loadingPromises.delete(url);
        resolve(img);
      };

      img.onerror = () => {
        this.loadingPromises.delete(url);
        reject(new Error(`Failed to load image: ${url}`));
      };

      // 设置跨域属性，如果需要的话
      img.crossOrigin = 'anonymous';
      img.src = url;
    });

    this.loadingPromises.set(url, loadPromise);
    return loadPromise;
  }

  preloadImages(urls: string[]): Promise<HTMLImageElement[]> {
    return Promise.all(urls.map(url => this.loadImage(url).catch(() => null)))
      .then(results => results.filter(Boolean) as HTMLImageElement[]);
  }

  getCachedImage(url: string): HTMLImageElement | null {
    return this.cache.get(url) || null;
  }

  clearCache(): void {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  getCacheSize(): number {
    return this.cache.size;
  }

  // 清理超过一定数量的缓存
  cleanup(maxSize: number = 100): void {
    if (this.cache.size <= maxSize) return;

    const entries = Array.from(this.cache.entries());
    const toDelete = entries.slice(0, this.cache.size - maxSize);
    
    toDelete.forEach(([url]) => {
      this.cache.delete(url);
    });
  }
}

// 创建全局实例
export const imageCache = new ImageCache();

// 预加载函数
export const preloadImages = (urls: string[]) => {
  return imageCache.preloadImages(urls);
};
